package com.evoapps.evoshift.ui.user

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.PhotoCamera
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.ClockFlag
import com.evoapps.evoshift.domain.model.UnplannedKind
import com.evoapps.evoshift.domain.usecase.shift.ClockReviewItem
import com.evoapps.evoshift.photo.decodeImage
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.ClockReviewViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.clock_review_adjust
import evoshift.composeapp.generated.resources.clock_review_approve
import evoshift.composeapp.generated.resources.clock_review_clocked
import evoshift.composeapp.generated.resources.clock_review_clocked_in
import evoshift.composeapp.generated.resources.clock_review_confirm
import evoshift.composeapp.generated.resources.clock_review_edit
import evoshift.composeapp.generated.resources.clock_review_edit_end
import evoshift.composeapp.generated.resources.clock_review_edit_start
import evoshift.composeapp.generated.resources.clock_review_extra_minutes
import evoshift.composeapp.generated.resources.clock_review_flag_early_in
import evoshift.composeapp.generated.resources.clock_review_flag_late_out
import evoshift.composeapp.generated.resources.clock_review_flag_no_app_activity
import evoshift.composeapp.generated.resources.clock_review_flag_clock_drift
import evoshift.composeapp.generated.resources.clock_review_flag_clock_drift_minutes
import evoshift.composeapp.generated.resources.clock_review_flag_outside_area
import evoshift.composeapp.generated.resources.clock_review_flag_overrun
import evoshift.composeapp.generated.resources.clock_review_flag_same_tablet
import evoshift.composeapp.generated.resources.clock_review_flag_very_short
import evoshift.composeapp.generated.resources.clock_review_photo
import evoshift.composeapp.generated.resources.clock_review_photo_missing
import evoshift.composeapp.generated.resources.clock_review_photo_note
import evoshift.composeapp.generated.resources.clock_review_photo_title
import evoshift.composeapp.generated.resources.clock_review_planned
import evoshift.composeapp.generated.resources.clock_review_reject
import evoshift.composeapp.generated.resources.clock_review_title
import evoshift.composeapp.generated.resources.clock_review_unplanned_cover
import evoshift.composeapp.generated.resources.clock_review_unplanned_extra
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.save
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** An item's test tag (robots find a shift's row). */
fun clockReviewRowTag(shiftId: String) = "clock_review_$shiftId"

/** The card's test tag. */
const val CLOCK_REVIEW_CARD_TAG = "clock_review_card"

private val FlagColor = Color(0xFFB45309)

/**
 * SH-134: "Do sprawdzenia" (to review), above Today at work and in the daily records: each flagged
 * clock (what is unusual, planned and clocked times, minutes not counted yet) or unplanned clock-in
 * ("Anna instead of Kasia") with the manager's decisions. Shows nothing when there is nothing to
 * review or the reader may not see it. [today]: the current workplace's day; else [day] =
 * (workplaceId, rosterId, date) of the daily records.
 */
@Composable
fun ClockReviewCard(
    viewModel: ClockReviewViewModel,
    modifier: Modifier = Modifier,
    day: Triple<String, String, String>? = null,
    /** On the navy day pane / dialog of the daily records (white text, no card). */
    onDark: Boolean = false
) {
    LaunchedEffect(day) {
        if (day == null) viewModel.showToday() else viewModel.showDay(day.first, day.second, day.third)
    }
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val note = (state.error ?: state.message)?.asString()
    LaunchedEffect(note) {
        if (note != null) {
            delay(4_000)
            viewModel.messageShown()
        }
    }
    if (!state.visible || (state.items.isEmpty() && note == null)) return
    val ink = if (onDark) Color.White else DeepNavy
    val accent = if (onDark) LightGold else DeepNavy
    val body: @Composable () -> Unit = {
        Column(modifier = if (onDark) Modifier.fillMaxWidth() else Modifier.padding(16.dp)) {
            Text(
                text = stringResource(Res.string.clock_review_title),
                style = (if (onDark) MaterialTheme.typography.titleSmall else MaterialTheme.typography.titleMedium).copy(fontWeight = FontWeight.Bold),
                color = ink.copy(alpha = if (onDark) 1f else 0.7f)
            )
            note?.let {
                Spacer(Modifier.height(4.dp))
                Text(it, style = MaterialTheme.typography.bodyMedium, color = if (state.error != null) Color(0xFFD32F2F) else Color(0xFF2E7D32))
            }
            state.items.forEachIndexed { index, item ->
                if (index > 0) HorizontalDivider(color = ink.copy(alpha = 0.12f))
                ClockReviewRow(
                    item = item,
                    ink = ink,
                    accent = accent,
                    canDecide = state.canDecide && state.busyShiftId == null,
                    onApprove = { viewModel.approve(item) },
                    onAdjust = { viewModel.adjustToPlan(item) },
                    onEdit = { viewModel.startEdit(item) },
                    onConfirm = { viewModel.confirmUnplanned(item) },
                    onReject = { viewModel.rejectUnplanned(item) },
                    onPhoto = { viewModel.openPhoto(item) }
                )
            }
        }
    }
    if (onDark) {
        Box(modifier.fillMaxWidth().testTag(CLOCK_REVIEW_CARD_TAG)) { body() }
    } else {
        RegularCard(modifier = modifier.fillMaxWidth().testTag(CLOCK_REVIEW_CARD_TAG)) { body() }
    }
    state.editing?.let { draft ->
        EvoDialog(
            title = stringResource(Res.string.clock_review_edit),
            onDismiss = viewModel::dismissEdit,
            subtitle = com.evoapps.evoshift.ui.util.personName(draft.item.workerName),
            confirm = DialogAction(stringResource(Res.string.save), viewModel::saveEdit)
        ) {
            EvoTextField(
                value = draft.start,
                onValueChange = viewModel::updateEditStart,
                label = stringResource(Res.string.clock_review_edit_start),
                keyboardType = KeyboardType.Number
            )
            EvoTextField(
                value = draft.end,
                onValueChange = viewModel::updateEditEnd,
                label = stringResource(Res.string.clock_review_edit_end),
                keyboardType = KeyboardType.Number
            )
        }
    }
    state.photo?.let { photo ->
        EvoDialog(
            title = stringResource(Res.string.clock_review_photo_title),
            icon = Icons.Default.PhotoCamera,
            onDismiss = viewModel::closePhoto,
            dismiss = DialogAction(stringResource(Res.string.close), viewModel::closePhoto)
        ) {
            val image = remember(photo.jpeg) { photo.jpeg?.let { runCatching { decodeImage(it, 1024) }.getOrNull() } }
            when {
                photo.loading -> CircularProgressIndicator()
                image != null -> Image(
                    bitmap = image,
                    contentDescription = stringResource(Res.string.clock_review_photo_title),
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxWidth().heightIn(max = 360.dp).clip(RoundedCornerShape(12.dp))
                )
                else -> Text(stringResource(Res.string.clock_review_photo_missing), color = Color.White.copy(alpha = 0.85f))
            }
            Text(stringResource(Res.string.clock_review_photo_note), color = Color.White.copy(alpha = 0.85f), style = MaterialTheme.typography.bodySmall)
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ClockReviewRow(
    item: ClockReviewItem,
    ink: Color,
    accent: Color,
    canDecide: Boolean,
    onApprove: () -> Unit,
    onAdjust: () -> Unit,
    onEdit: () -> Unit,
    onConfirm: () -> Unit,
    onReject: () -> Unit,
    onPhoto: () -> Unit
) {
    Column(Modifier.fillMaxWidth().testTag(clockReviewRowTag(item.shiftId)).padding(vertical = 8.dp)) {
        val worker = com.evoapps.evoshift.ui.util.personName(item.workerName)
        val title = when {
            item.isUnplanned && item.unplanned?.kind == UnplannedKind.COVER ->
                stringResource(Res.string.clock_review_unplanned_cover, worker, item.coveredName ?: "?")
            item.isUnplanned -> stringResource(Res.string.clock_review_unplanned_extra, worker)
            else -> worker
        }
        Text(title, style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold), color = ink)
        if (!item.isExtraShift) {
            Text(
                stringResource(Res.string.clock_review_planned, hourOf(item.plannedStart), hourOf(item.plannedEnd)),
                style = MaterialTheme.typography.bodyMedium,
                color = ink.copy(alpha = 0.8f)
            )
        }
        item.actualStart?.let { start ->
            Text(
                item.actualEnd?.let { stringResource(Res.string.clock_review_clocked, hourOf(start), hourOf(it)) }
                    ?: stringResource(Res.string.clock_review_clocked_in, hourOf(start)),
                style = MaterialTheme.typography.bodyMedium,
                color = ink.copy(alpha = 0.8f)
            )
        }
        if (item.flags.isNotEmpty()) {
            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(4.dp), modifier = Modifier.padding(top = 4.dp)) {
                item.flags.forEach { flag ->
                    val drift = item.driftMinutes?.takeIf { flag == ClockFlag.CLOCK_DRIFT }
                    FlagChip(
                        if (drift != null) pluralStringResource(Res.plurals.clock_review_flag_clock_drift_minutes, kotlin.math.abs(drift), kotlin.math.abs(drift))
                        else stringResource(clockFlagLabel(flag))
                    )
                }
            }
        }
        if (item.extraMinutes > 0) {
            Text(
                stringResource(Res.string.clock_review_extra_minutes, item.extraMinutes.toInt()),
                style = MaterialTheme.typography.labelMedium,
                color = FlagColor
            )
        }
        val outlined = ButtonDefaults.outlinedButtonColors(contentColor = accent)
        val text = ButtonDefaults.textButtonColors(contentColor = accent)
        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(top = 4.dp)) {
            if (item.isUnplanned) {
                OutlinedButton(onClick = onConfirm, enabled = canDecide, colors = outlined) { Text(stringResource(Res.string.clock_review_confirm)) }
                TextButton(onClick = onReject, enabled = canDecide, colors = text) { Text(stringResource(Res.string.clock_review_reject)) }
            } else {
                OutlinedButton(onClick = onApprove, enabled = canDecide, colors = outlined) { Text(stringResource(Res.string.clock_review_approve)) }
                if (item.canAdjustToPlan) {
                    TextButton(onClick = onAdjust, enabled = canDecide, colors = text) { Text(stringResource(Res.string.clock_review_adjust)) }
                }
                TextButton(onClick = onEdit, enabled = canDecide, colors = text) { Text(stringResource(Res.string.clock_review_edit)) }
            }
            if (item.photoPath != null) {
                TextButton(onClick = onPhoto, colors = text) { Text(stringResource(Res.string.clock_review_photo)) }
            }
        }
    }
}

@Composable
private fun FlagChip(text: String) {
    Box(
        Modifier.clip(RoundedCornerShape(50)).background(FlagColor.copy(alpha = 0.14f)).padding(horizontal = 10.dp, vertical = 4.dp)
    ) {
        Text(text, style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = FlagColor)
    }
}

fun clockFlagLabel(flag: ClockFlag): StringResource = when (flag) {
    ClockFlag.EARLY_IN -> Res.string.clock_review_flag_early_in
    ClockFlag.LATE_OUT -> Res.string.clock_review_flag_late_out
    ClockFlag.OVERRUN -> Res.string.clock_review_flag_overrun
    ClockFlag.VERY_SHORT -> Res.string.clock_review_flag_very_short
    ClockFlag.OUTSIDE_AREA -> Res.string.clock_review_flag_outside_area
    ClockFlag.SAME_TABLET -> Res.string.clock_review_flag_same_tablet
    ClockFlag.NO_APP_ACTIVITY -> Res.string.clock_review_flag_no_app_activity
    ClockFlag.CLOCK_DRIFT -> Res.string.clock_review_flag_clock_drift
}
